import { useMemo } from 'react';
import { addDays, addWeeks, startOfWeek, subWeeks } from 'date-fns';
import type { OrganisationTimesheetSettings } from '@/types/timesheetSettings';
import type { RejectionRecord } from '@/types/rejection';
import { TimesheetInlineAlerts } from '../status-banners/TimesheetInlineAlerts';
import { WeekNavDesktop } from './WeekNavDesktop';
import { WeekNavTablet } from './WeekNavTablet';
import { WeekNavMobile } from './WeekNavMobile';
import type { WeekNavVariantProps } from './weekNavTypes';

export interface TimesheetWeekNavProps {
  currentWeekStart: Date;
  weekStartDay: 0 | 1 | 2 | 3 | 4 | 5 | 6;
  status: string;
  organizationSettings: OrganisationTimesheetSettings | undefined;
  isViewingOtherResource: boolean;
  isRejected: boolean;
  pendingRejection: RejectionRecord | null;
  onNavigate: (direction: 'prev' | 'next') => void;
  onNavigateToDate: (date: Date) => void;
}

/**
 * Week navigation toolbar. Computes the navigation bounds from the organisation
 * settings and renders the responsive layout variant for the current viewport.
 */
export function TimesheetWeekNav({
  currentWeekStart,
  weekStartDay,
  status,
  organizationSettings,
  isViewingOtherResource,
  isRejected,
  pendingRejection,
  onNavigate,
  onNavigateToDate,
}: TimesheetWeekNavProps) {
  const canNavigatePrev = useMemo(() => {
    const limit = organizationSettings?.historicalEntryLimit ?? 4;
    const earliest = subWeeks(startOfWeek(new Date(), { weekStartsOn: weekStartDay }), limit);
    return subWeeks(currentWeekStart, 1) >= earliest;
  }, [currentWeekStart, organizationSettings?.historicalEntryLimit, weekStartDay]);

  const canNavigateNext = useMemo(() => {
    const setting = organizationSettings?.allowFutureDatedEntries ?? '2weeks';
    if (setting === 'unlimited') return true;
    const now = startOfWeek(new Date(), { weekStartsOn: weekStartDay });
    if (setting === 'no') return addWeeks(currentWeekStart, 1) <= now;
    const weeksAhead = setting === '1week' ? 1 : setting === '2weeks' ? 2 : 4;
    return addWeeks(currentWeekStart, 1) <= addWeeks(now, weeksAhead);
  }, [currentWeekStart, organizationSettings?.allowFutureDatedEntries, weekStartDay]);

  const minAllowedDate = useMemo(() => {
    const limit = organizationSettings?.historicalEntryLimit ?? 4;
    return subWeeks(startOfWeek(new Date(), { weekStartsOn: weekStartDay }), limit);
  }, [organizationSettings?.historicalEntryLimit, weekStartDay]);

  const maxAllowedDate = useMemo(() => {
    const setting = organizationSettings?.allowFutureDatedEntries ?? '2weeks';
    const now = startOfWeek(new Date(), { weekStartsOn: weekStartDay });
    if (setting === 'unlimited') return undefined;
    if (setting === 'no') return addDays(now, 6);
    const weeksAhead = setting === '1week' ? 1 : setting === '2weeks' ? 2 : 4;
    return addDays(addWeeks(now, weeksAhead), 6);
  }, [organizationSettings?.allowFutureDatedEntries, weekStartDay]);

  const variantProps: WeekNavVariantProps = {
    currentWeekStart,
    status,
    isViewingOtherResource,
    canNavigatePrev,
    canNavigateNext,
    minAllowedDate,
    maxAllowedDate,
    onNavigate,
    onNavigateToDate,
  };

  const alertProps = {
    isRejected,
    pendingRejection,
    organizationSettings,
    currentWeekStart,
    status,
  };

  return (
    <>
      {/* Desktop Layout (≥1024px) - Simplified Single Row */}
      <div className="hidden lg:flex items-center justify-between gap-3 w-full">
        <WeekNavDesktop {...variantProps} />
        <TimesheetInlineAlerts variant="desktop" {...alertProps} />
      </div>

      {/* Tablet Layout (768px - 1023px) - Compact Single Row */}
      <div className="hidden md:flex lg:hidden items-center justify-between gap-2 w-full">
        <WeekNavTablet {...variantProps} />
        <TimesheetInlineAlerts variant="tablet" {...alertProps} />
      </div>

      {/* Mobile Layout (<768px) - Simplified */}
      <div className="flex md:hidden flex-col gap-3">
        <WeekNavMobile {...variantProps} />
        <TimesheetInlineAlerts variant="mobile" {...alertProps} />
      </div>
    </>
  );
}
